import { supabase } from '@/lib/supabase/client';
import type { Quote, QuoteInsert, QuoteUpdate, QuoteItem, QuoteItemInsert } from '@/types/quotes';
import { componentPricingService } from './component-pricing';
import { normalizeAgreementTerms } from '@/types/agreement-terms';
import { normalizeQuoteAgreementTerms } from '@/lib/sla/normalize';
import { dealsService } from './deals';
import { invoicesService } from './invoices';
import { locationsService } from './locations';
import { customerNameForUnassignedDuplicate, suggestDuplicateDocumentNumber } from '@/lib/utils/sales-document-numbers';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

async function updateQuoteStatusRecord(quoteId: string, status: Quote['status']): Promise<Quote> {
  const { data: updatedQuote, error } = await supabase
    .from('quotes')
    .update({ status })
    .eq('id', quoteId)
    .select()
    .single();

  if (error) {
    console.error('Error updating quote status:', error);
    throw new Error(`Failed to update quote status: ${error.message}`);
  }

  if (!updatedQuote) {
    throw new Error('Quote not found');
  }

  return normalizeQuoteAgreementTerms(updatedQuote as Quote);
}

type QuoteItemDraft = Omit<QuoteItemInsert, 'quote_id'>;

export const quotesService = {
  async getAll(): Promise<Quote[]> {
    try {
      const { data, error } = await supabase.from('quotes').select('*').order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching quotes:', error);
        return [];
      }

      return ((data as Quote[]) || []).map((quote) => normalizeQuoteAgreementTerms(quote));
    } catch (error) {
      console.error('Error fetching quotes:', error);
      return [];
    }
  },

  async getById(id: string): Promise<Quote | null> {
    try {
      const { data, error } = await supabase.from('quotes').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching quote:', error);
        return null;
      }

      return data ? normalizeQuoteAgreementTerms(data as Quote) : null;
    } catch (error) {
      console.error('Error fetching quote:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<Quote[]> {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching quotes by location ID:', error);
        return [];
      }

      return ((data as Quote[]) || []).map((quote) => normalizeQuoteAgreementTerms(quote));
    } catch (error) {
      console.error('Error fetching quotes by location ID:', error);
      return [];
    }
  },

  async getItemsByQuoteId(quoteId: string): Promise<QuoteItem[]> {
    try {
      const { data, error } = await supabase
        .from('quote_items')
        .select('*')
        .eq('quote_id', quoteId)
        .order('line_number', { ascending: true });

      if (error) {
        console.error('Error fetching quote items:', error);
        return [];
      }

      return (data as QuoteItem[]) || [];
    } catch (error) {
      console.error('Error fetching quote items:', error);
      return [];
    }
  },

  async create(data: QuoteInsert, items: Omit<QuoteItemInsert, 'quote_id'>[] = []): Promise<Quote> {
    try {
      const userId = await getCurrentUserId();

      // Calculate prices for items with component_id
      const itemsWithPrices: QuoteItemDraft[] = await Promise.all(
        items.map(async (item): Promise<QuoteItemDraft> => {
          let unitPrice = item.unit_price;
          let lineTotal = item.line_total;

          // If item has component_id, fetch price and calculate totals
          if (item.component_id) {
            try {
              unitPrice = await componentPricingService.getPriceForComponent(item.component_id);
              lineTotal = item.quantity * unitPrice;
            } catch (error) {
              console.error(`Error getting price for component ${item.component_id}:`, error);
              // Use provided values if price lookup fails
              unitPrice = item.unit_price;
              lineTotal = item.line_total;
            }
          }

          return {
            line_number: item.line_number,
            component_id: item.component_id ?? null,
            display_type_id: item.display_type_id ?? null,
            description: item.description,
            notes: item.notes ?? null,
            quantity: item.quantity,
            unit_price: unitPrice,
            line_total: lineTotal,
          };
        })
      );

      // Calculate subtotal, tax, and total
      const subtotal = itemsWithPrices.reduce((sum, item) => sum + item.line_total, 0);
      const tax = 0; // Placeholder for now
      const total = subtotal + tax;

      const insertData = {
        location_id: data.location_id ?? null,
        contact_id: data.contact_id ?? null,
        customer_name: data.customer_name ?? null,
        deal_id: data.deal_id ?? null,
        quote_number: data.quote_number,
        status: data.status ?? 'draft',
        valid_until: data.valid_until ?? null,
        subtotal: data.subtotal ?? subtotal,
        tax: data.tax ?? tax,
        total: data.total ?? total,
        notes: data.notes ?? null,
        agreement_terms_json: normalizeAgreementTerms(data.agreement_terms),
        created_by: userId,
      };

      const { data: newQuote, error } = await supabase.from('quotes').insert(insertData).select().single();

      if (error) {
        console.error('Error creating quote:', error);
        throw new Error(`Failed to create quote: ${error.message}`);
      }

      // Create quote items if provided
      if (itemsWithPrices.length > 0 && newQuote) {
        const itemsToInsert: QuoteItemInsert[] = itemsWithPrices.map((item) => ({
          quote_id: newQuote.id,
          line_number: item.line_number,
          component_id: item.component_id ?? null,
          display_type_id: item.display_type_id ?? null,
          description: item.description,
          notes: item.notes ?? null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_total: item.line_total,
        }));

        const { error: itemsError } = await supabase.from('quote_items').insert(itemsToInsert);

        if (itemsError) {
          console.error('Error creating quote items:', itemsError);
          // Rollback quote creation
          await supabase.from('quotes').delete().eq('id', newQuote.id);
          throw new Error(`Failed to create quote items: ${itemsError.message}`);
        }
      }

      return normalizeQuoteAgreementTerms({
        ...(newQuote as Quote),
        agreement_terms: (newQuote as any).agreement_terms_json ?? null,
      });
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  },

  async update(id: string, data: QuoteUpdate): Promise<Quote> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.location_id !== undefined) updateData.location_id = data.location_id;
      if (data.contact_id !== undefined) updateData.contact_id = data.contact_id;
      if (data.customer_name !== undefined) updateData.customer_name = data.customer_name;
      if (data.deal_id !== undefined) updateData.deal_id = data.deal_id;
      if (data.quote_number !== undefined) updateData.quote_number = data.quote_number;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.valid_until !== undefined) updateData.valid_until = data.valid_until;
      if (data.subtotal !== undefined) updateData.subtotal = data.subtotal;
      if (data.tax !== undefined) updateData.tax = data.tax;
      if (data.total !== undefined) updateData.total = data.total;
      if (data.notes !== undefined) updateData.notes = data.notes;
      if (data.agreement_terms !== undefined) updateData.agreement_terms_json = normalizeAgreementTerms(data.agreement_terms);

      const { data: updatedQuote, error } = await supabase
        .from('quotes')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating quote:', error);
        throw new Error(`Failed to update quote: ${error.message}`);
      }

      if (!updatedQuote) {
        throw new Error('Quote not found');
      }

      return normalizeQuoteAgreementTerms({
        ...(updatedQuote as Quote),
        agreement_terms: (updatedQuote as any).agreement_terms_json ?? null,
      });
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  },

  async replaceItems(quoteId: string, items: Omit<QuoteItemInsert, 'quote_id'>[]): Promise<void> {
    try {
      // Calculate prices for items with component_id
      const itemsWithPrices: QuoteItemDraft[] = await Promise.all(
        items.map(async (item): Promise<QuoteItemDraft> => {
          let unitPrice = item.unit_price;
          let lineTotal = item.line_total;

          // If item has component_id, fetch price and calculate totals
          if (item.component_id) {
            try {
              unitPrice = await componentPricingService.getPriceForComponent(item.component_id);
              lineTotal = item.quantity * unitPrice;
            } catch (error) {
              console.error(`Error getting price for component ${item.component_id}:`, error);
              // Use provided values if price lookup fails
              unitPrice = item.unit_price;
              lineTotal = item.line_total;
            }
          }

          return {
            line_number: item.line_number,
            component_id: item.component_id ?? null,
            display_type_id: item.display_type_id ?? null,
            description: item.description,
            notes: item.notes ?? null,
            quantity: item.quantity,
            unit_price: unitPrice,
            line_total: lineTotal,
          };
        })
      );

      // Calculate subtotal, tax, and total
      const subtotal = itemsWithPrices.reduce((sum, item) => sum + item.line_total, 0);
      const tax = 0; // Placeholder for now
      const total = subtotal + tax;

      // Update quote totals
      await this.update(quoteId, {
        subtotal,
        tax,
        total,
      });

      // Delete existing items
      const { error: deleteError } = await supabase.from('quote_items').delete().eq('quote_id', quoteId);

      if (deleteError) {
        console.error('Error deleting quote items:', deleteError);
        throw new Error(`Failed to delete quote items: ${deleteError.message}`);
      }

      // Insert new items
      if (itemsWithPrices.length > 0) {
        const itemsToInsert: QuoteItemInsert[] = itemsWithPrices.map((item) => ({
          quote_id: quoteId,
          line_number: item.line_number,
          component_id: item.component_id ?? null,
          display_type_id: item.display_type_id ?? null,
          description: item.description,
          notes: item.notes ?? null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_total: item.line_total,
        }));

        const { error: insertError } = await supabase.from('quote_items').insert(itemsToInsert);

        if (insertError) {
          console.error('Error creating quote items:', insertError);
          throw new Error(`Failed to create quote items: ${insertError.message}`);
        }
      }
    } catch (error) {
      console.error('Error replacing quote items:', error);
      throw error;
    }
  },

  async setStatus(quoteId: string, status: Quote['status']): Promise<Quote> {
    try {
      if (status === 'converted') {
        throw new Error('Use quote conversion to create an invoice and mark a quote as converted');
      }

      return await updateQuoteStatusRecord(quoteId, status);
    } catch (error) {
      console.error('Error updating quote status:', error);
      throw error;
    }
  },

  async convertToDeal(quoteId: string): Promise<string> {
    try {
      const quote = await this.getById(quoteId);
      if (!quote) {
        throw new Error('Quote not found');
      }

      // Create deal from quote
      const deal = await dealsService.create({
        location_id: quote.location_id,
        contact_id: quote.contact_id,
        name: `Deal from Quote ${quote.quote_number}`,
        stage: 'qualified',
        total_contract_value: quote.total,
        upfront_amount: quote.total,
        notes: `Converted from quote ${quote.quote_number}. ${quote.notes || ''}`,
      });

      // Update quote status to accepted
      await this.setStatus(quoteId, 'accepted');

      return deal.id;
    } catch (error) {
      console.error('Error converting quote to deal:', error);
      throw error;
    }
  },

  async convertToInvoice(quoteId: string): Promise<string> {
    try {
      const quote = await this.getById(quoteId);
      if (!quote) {
        throw new Error('Quote not found');
      }

      const items = await this.getItemsByQuoteId(quoteId);

      let resolvedLocationId = quote.location_id;
      if (!resolvedLocationId && quote.deal_id) {
        const linkedDeal = await dealsService.getById(quote.deal_id);
        resolvedLocationId = linkedDeal?.location_id || null;
      }

      let resolvedCustomerName = quote.customer_name?.trim() || null;
      if (!resolvedCustomerName && resolvedLocationId) {
        const linkedLocation = await locationsService.getById(resolvedLocationId);
        resolvedCustomerName = linkedLocation?.location_name || null;
      }
      if (!resolvedCustomerName) {
        resolvedCustomerName = `Invoice for ${quote.quote_number}`;
      }

      // Generate invoice number (simple format: INV-YYYY-XXX)
      const year = new Date().getFullYear();
      const existingInvoices = await invoicesService.getAll();
      const invoiceNumbers = existingInvoices
        .map((inv) => inv.invoice_number)
        .filter((num) => num.startsWith(`INV-${year}-`))
        .map((num) => {
          const match = num.match(/INV-\d{4}-(\d+)/);
          const suffix = match?.[1];
          return suffix ? parseInt(suffix, 10) : 0;
        });
      const nextNumber = invoiceNumbers.length > 0 ? Math.max(...invoiceNumbers) + 1 : 1;
      const invoiceNumber = `INV-${year}-${String(nextNumber).padStart(3, '0')}`;

      // Convert quote items to invoice line items
      const lineItems = items.map((item) => ({
        line_number: item.line_number,
        component_id: item.component_id || null,
        display_type_id: item.display_type_id || null,
        description: item.description,
        notes: item.notes || null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.line_total,
      }));

      // Create invoice from quote
      const invoice = await invoicesService.create({
        location_id: resolvedLocationId,
        deal_id: quote.deal_id || null,
        quote_id: quote.id,
        location_order_id: null,
        invoice_number: invoiceNumber,
        customer_name: resolvedCustomerName,
        status: 'draft',
        due_date: null,
        line_items: lineItems,
        subtotal: quote.subtotal,
        tax: quote.tax,
        total: quote.total,
        notes: `Converted from quote ${quote.quote_number}. ${quote.notes || ''}`,
        agreement_terms: quote.agreement_terms,
      });

      // Update quote status to converted
      // If this fails, the invoice is still created, but we log the error
      try {
        await updateQuoteStatusRecord(quoteId, 'converted');
      } catch (statusError) {
        console.error('Error updating quote status to converted:', statusError);
        // If the constraint doesn't allow 'converted' status yet, 
        // the migration needs to be run, but we still return the invoice ID
        // so the user can see the invoice was created
        if (statusError instanceof Error && statusError.message.includes('check constraint')) {
          console.warn('Quote status update failed - migration may need to be applied. Invoice was created successfully.');
        } else {
          throw statusError;
        }
      }

      return invoice.id;
    } catch (error) {
      console.error('Error converting quote to invoice:', error);
      throw error;
    }
  },

  async duplicate(
    sourceQuoteId: string,
    overrides?: { quote_number?: string; customer_name?: string | null; contact_id?: string | null; location_id?: string | null; deal_id?: string | null }
  ): Promise<Quote> {
    const source = await this.getById(sourceQuoteId);
    if (!source) {
      throw new Error('Quote not found');
    }

    const items = await this.getItemsByQuoteId(sourceQuoteId);
    const allQuotes = await this.getAll();
    const quoteNumber =
      overrides?.quote_number?.trim() ||
      suggestDuplicateDocumentNumber(
        source.quote_number,
        allQuotes.map((quote) => quote.quote_number)
      );

    const lineItems = items.map((item) => ({
      line_number: item.line_number,
      component_id: item.component_id ?? null,
      display_type_id: item.display_type_id ?? null,
      description: item.description,
      notes: item.notes ?? null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      line_total: item.line_total,
    }));

    const duplicatedNotes = source.notes
      ? `${source.notes}\n\nDuplicated from ${source.quote_number}.`
      : `Duplicated from ${source.quote_number}.`;

    const locationId = overrides?.location_id !== undefined ? overrides.location_id : null;
    const customerName = overrides?.customer_name !== undefined ? overrides.customer_name : null;

    return this.create(
      {
        location_id: locationId,
        contact_id: overrides?.contact_id !== undefined ? overrides.contact_id : null,
        customer_name: locationId ? customerName : customerNameForUnassignedDuplicate(customerName),
        deal_id: overrides?.deal_id !== undefined ? overrides.deal_id : null,
        quote_number: quoteNumber,
        status: 'draft',
        valid_until: source.valid_until,
        subtotal: source.subtotal,
        tax: source.tax,
        total: source.total,
        notes: duplicatedNotes,
        agreement_terms: source.agreement_terms,
      },
      lineItems
    );
  },

  async delete(id: string): Promise<void> {
    try {
      // Delete items first (CASCADE should handle this, but being explicit)
      await supabase.from('quote_items').delete().eq('quote_id', id);

      const { error } = await supabase.from('quotes').delete().eq('id', id);

      if (error) {
        console.error('Error deleting quote:', error);
        throw new Error(`Failed to delete quote: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  },
};
